import { Alert, Input, Typography } from 'antd';
import classNames from 'classnames';
import FormField from 'components/FormField';
import InGuideSpotSelector from 'components/InGuideSpotSelector';
import { MediaSelector } from 'components/MediaSelector';
import {
    GUIDE_GOOD_TO_KNOW_DESCRIPTION_MAX_LENGTH,
    GUIDE_HIGHLIGHT_TITLE_MAX_LENGTH,
} from 'constants/fields-limits';
import { HIGHLIGHT_COVER_RATIO } from 'constants/media-sizes.constants';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    initGuideHighlights,
    notifyGuideHighlightsChange,
    resetGuideHighlights,
} from 'store/guide-editor/actions';
import {
    doHighlightsHaveErrors,
    getCurrentGuide,
    getCurrentHighlightsBeingEdited,
} from 'store/guide-editor/selectors';
import {
    getErrorLoadingHighlightsForGuide,
    isLoadingHighlightsForGuide,
    isSavingHighlightItems,
} from 'store/highlight-items/selectors';
import { ItemFieldChange, applyPatchToItem } from 'utils/sortable-item-utils';
import { v4 as uuid } from 'uuid';
import { SortableList } from '../SortableList';
import { useSort } from '../SortableList/use-sort';
import styles from './style.module.css';

type GuideEditorHighlightItemProps = {
    index: number;
    guide: Guide;
    highlight: HighlightItem;
    onChangeField: (change: ItemFieldChange) => void;
    errors?: FieldError;
};

export const GuideEditorHighlights = () => {
    const dispatch = useDispatch();

    const guide = useSelector(getCurrentGuide);
    const guideId = useMemo(() => guide?.id, [guide]);
    const currentHighlights = useSelector(getCurrentHighlightsBeingEdited);
    const isLoading = useSelector((state: RootState) =>
        isLoadingHighlightsForGuide(state, guideId),
    );
    const errorLoading = useSelector((state: RootState) =>
        getErrorLoadingHighlightsForGuide(state, guideId),
    );
    const isSaving = useSelector(isSavingHighlightItems);
    const { errors } = useSelector(doHighlightsHaveErrors);
    const { onMoveUp, onMoveDown } = useSort(currentHighlights);

    const patchHighlightFields = (changes: ItemFieldChange[]) => {
        let highlights = currentHighlights;
        for (const change of changes) {
            highlights = applyPatchToItem(highlights, change);
        }

        dispatch(notifyGuideHighlightsChange(highlights));
    };

    const patchHighlightField = (changes: ItemFieldChange) => {
        patchHighlightFields([changes]);
    };

    const addHighlight = () => {
        if (!guideId) return;
        const newHighlight = getEmptyHighlight(
            guideId,
            currentHighlights.length,
        );
        const newHighlights = currentHighlights.concat([newHighlight]);
        dispatch(notifyGuideHighlightsChange(newHighlights));
    };

    const deleteHighlight = ({ id: highlightId }: HighlightItem) => {
        const newHighlights = currentHighlights.filter(
            (highlight) => highlight.id !== highlightId,
        );
        dispatch(notifyGuideHighlightsChange(newHighlights));
    };

    const moveHighlightUp = (highlight: HighlightItem) => {
        const sortedItems = onMoveUp(highlight);

        if (sortedItems) {
            patchHighlightFields(
                sortedItems.map((item) => ({
                    id: item.id,
                    fieldName: 'order',
                    newValue: item.newOrder,
                })),
            );
        }
    };

    const moveHighlightDown = (highlight: HighlightItem) => {
        const sortedItems = onMoveDown(highlight);

        if (sortedItems) {
            patchHighlightFields(
                sortedItems.map((item) => ({
                    id: item.id,
                    fieldName: 'order',
                    newValue: item.newOrder,
                })),
            );
        }
    };

    useEffect(() => {
        if (guideId) {
            dispatch(initGuideHighlights(guideId));
        }
        return () => {
            dispatch(resetGuideHighlights());
        };
    }, [guideId, dispatch]);

    if (!guide) {
        return null;
    }

    return (
        <>
            {errorLoading && (
                <div className="full-page-tabs-error">
                    <Alert
                        className="grow-full-flex"
                        message="There was an error loading highlight items"
                        description={errorLoading}
                        type="error"
                        showIcon
                    />
                </div>
            )}
            <div className="guide-editor-read-only-content">
                <SortableList
                    title="Highlights"
                    data={currentHighlights}
                    onAdd={addHighlight}
                    onDelete={deleteHighlight}
                    onMoveDown={moveHighlightDown}
                    onMoveUp={moveHighlightUp}
                    isLoading={isLoading || isSaving}
                    isReadOnly
                >
                    {({ data, index }) =>
                        !data ? (
                            <SortableList.EmptyState
                                message="No Highlights"
                                action={null}
                            />
                        ) : (
                            <GuideEditorHighlightItem
                                key={data.id}
                                index={index}
                                highlight={data}
                                guide={guide}
                                onChangeField={patchHighlightField}
                                errors={errors[data.id]}
                            />
                        )
                    }
                </SortableList>
            </div>
        </>
    );
};

function GuideEditorHighlightItem({
    index,
    guide,
    highlight,
    onChangeField,
    errors = {},
}: GuideEditorHighlightItemProps) {
    const handleChangeField = useCallback(
        <T,>(fieldName: string, newValue: T) => {
            onChangeField({
                id: highlight.id,
                fieldName: fieldName,
                newValue: newValue,
            });
        },
        [highlight.id, onChangeField],
    );

    return (
        <div className={classNames(styles.item, 'js-highlight-item-editor')}>
            <Typography.Title level={4}>{`Item ${index + 1}`}</Typography.Title>
            <div className={styles.itemContent}>
                <div className={styles.mediaSelector}>
                    <MediaSelector
                        location={guide.location}
                        guideId={highlight.guide_id}
                        includeVideos={true}
                        onSelect={(media: CMSMedia) =>
                            handleChangeField('cms_media', media)
                        }
                        onDelete={() => handleChangeField('cms_media', null)}
                        media={highlight.cms_media}
                        ratio={HIGHLIGHT_COVER_RATIO}
                        spot={highlight.spot ?? undefined}
                    />
                </div>
                <div className={styles.form}>
                    <FormField
                        label="Title"
                        length={highlight.title.length}
                        maxLength={GUIDE_HIGHLIGHT_TITLE_MAX_LENGTH}
                        error={errors.title}
                        labelWidth={100}
                    >
                        <Input
                            className="form-field__textarea"
                            value={highlight.title}
                            readOnly
                            onChange={(e) =>
                                handleChangeField('title', e.target.value)
                            }
                            status={errors.title ? 'error' : ''}
                        />
                    </FormField>
                    <FormField
                        label="Description"
                        length={highlight.description.length}
                        maxLength={GUIDE_GOOD_TO_KNOW_DESCRIPTION_MAX_LENGTH}
                        error={errors.description}
                        labelWidth={100}
                    >
                        <Input.TextArea
                            className="form-field__textarea"
                            value={highlight.description}
                            readOnly
                            autoSize={{
                                minRows: 1,
                                maxRows: 4,
                            }}
                            status={errors.description ? 'error' : ''}
                            onChange={(e) =>
                                handleChangeField('description', e.target.value)
                            }
                        />
                    </FormField>
                    <FormField
                        label="Spot"
                        labelWidth={100}
                        error={errors.spot}
                    >
                        <InGuideSpotSelector
                            value={highlight.spot}
                            guide={guide}
                            onSelect={(spot: PSSpot_v15_Minimal | null) =>
                                handleChangeField('spot', spot)
                            }
                            hasError={!!errors.spot}
                        />
                    </FormField>
                    <FormField label="Notes" labelWidth={100}>
                        <Input.TextArea
                            className="form-field__textarea"
                            readOnly
                            autoSize={{
                                minRows: 1,
                                maxRows: 12,
                            }}
                            value={highlight.editor_notes}
                            onChange={(e) =>
                                handleChangeField(
                                    'editor_notes',
                                    e.target.value,
                                )
                            }
                            placeholder="Add your thoughts..."
                        />
                    </FormField>
                </div>
            </div>
        </div>
    );
}

function getEmptyHighlight(guideId: Id, order: number): HighlightItem {
    return {
        id: uuid(),
        guide_id: guideId,
        title: '',
        description: '',
        editor_notes: '',
        cms_media: null,
        spot: null,
        order: order,
    };
}
